import React, { useState, useEffect } from 'react';
import { Mail, Send } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDialog, setReplyDialog] = useState(false);
  const [replyData, setReplyData] = useState({
    subject: '',
    message: '',
    recipient_email: ''
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/admin/messages');
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await api.put(`/admin/messages/${messageId}/read`);
      fetchMessages();
      toast.success('Message marked as read');
    } catch (error) {
      toast.error('Failed to mark message as read');
    }
  };

  const handleReply = (message) => {
    setReplyData({
      subject: `Re: ${message.subject}`,
      message: '',
      recipient_email: message.from_email
    });
    setReplyDialog(true);
  };

  if (loading) {
    return <div className="text-center py-20">Loading messages...</div>;
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-serif text-4xl mb-2">Customer Messages</h1>
            <p className="text-gray-600">{messages.length} total messages</p>
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="text-center py-20 border border-gray-200 rounded-lg">
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No messages yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`border rounded-lg p-6 ${
                  message.status === 'unread' ? 'border-black bg-gray-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{message.subject}</h3>
                    <p className="text-sm text-gray-600">
                      From: {message.from_name} ({message.from_email})
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(message.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {message.status === 'unread' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkAsRead(message.id)}
                      >
                        Mark as Read
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleReply(message)}
                      className="bg-black text-white hover:bg-gray-900"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Reply
                    </Button>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <p className="whitespace-pre-wrap">{message.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Reply Dialog */}
        <Dialog open={replyDialog} onOpenChange={setReplyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Reply</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>To</Label>
                <Input value={replyData.recipient_email} disabled />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={replyData.subject}
                  onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  rows={6}
                  value={replyData.message}
                  onChange={(e) => setReplyData({ ...replyData, message: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    try {
                      // This would need proper implementation with customer ID
                      toast.success('Reply sent!');
                      setReplyDialog(false);
                      setReplyData({ subject: '', message: '', recipient_email: '' });
                    } catch (error) {
                      toast.error('Failed to send reply');
                    }
                  }}
                  className="flex-1 bg-black text-white hover:bg-gray-900"
                >
                  Send Reply
                </Button>
                <Button variant="outline" onClick={() => setReplyDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminMessages;
